import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { Field } from './Field';
import { Input } from './Input';

export type ConfirmOptions = {
  title: string;
  message?: ReactNode;
  /** Label of the confirm button (default "Confirm"). */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for destructive actions. */
  danger?: boolean;
  /**
   * Text the user must type exactly (surrounding whitespace ignored) before confirm is enabled. Use for
   * high-impact actions such as bulk sends above `settings.bulkConfirmThreshold`.
   */
  requireText?: string;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

type Pending = { options: ConfirmOptions; resolve: (value: boolean) => void };

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [typed, setTyped] = useState('');
  const pendingRef = useRef<Pending | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A new request supersedes an unanswered one (treated as cancelled).
        pendingRef.current?.resolve(false);
        const next = { options, resolve };
        pendingRef.current = next;
        setTyped('');
        setPending(next);
      }),
    [],
  );

  const settle = (value: boolean) => {
    pendingRef.current?.resolve(value);
    pendingRef.current = null;
    setPending(null);
  };

  const options = pending?.options;
  const requireText = options?.requireText;
  const canConfirm = !requireText || typed.trim() === requireText.trim();

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog
        open={pending !== null}
        onClose={() => settle(false)}
        title={options?.title ?? ''}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {options?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={options?.danger ? 'danger' : 'primary'}
              disabled={!canConfirm}
              onClick={() => settle(true)}
              data-autofocus={requireText ? undefined : true}
            >
              {options?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        {options?.message && <div className="leading-relaxed">{options.message}</div>}
        {requireText && (
          <form
            className="mt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (canConfirm) settle(true);
            }}
          >
            <Field
              label={
                <>
                  Type <span className="rounded bg-slate-100 px-1 font-mono text-slate-900">{requireText}</span> to confirm
                </>
              }
            >
              <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} data-autofocus />
            </Field>
          </form>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}

/**
 * `const confirm = useConfirm(); if (await confirm({ title: 'Delete lead?', danger: true })) …`
 * Resolves false on cancel, Esc or backdrop click.
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used inside <ConfirmProvider>');
  return ctx;
}
